import { Button, Dialog } from 'components/ui'
import React, { useEffect, useState } from 'react'
import { apiWarrantyPaymentIntent } from 'services/LoveWarrantyServices'
import StripePaymentElement from 'views/stripe-payment'
import { popNotification } from '.'
import { HiCurrencyPound } from 'react-icons/hi'
import { apiGetCustomerSavedCards, apiGetMe, apiUpdatePaymentMethodTo, apiUpdateWarrantyToDirectDebit } from 'services/AuthService'
import classNames from 'classnames'
import isLastChild from 'utils/isLastChild'
import { getDirectDebitSchedule } from 'views/project/ScrumBoard/utils'
import { useSelector } from 'react-redux'

const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
]

const PaymentModal = ({ warranty, buttonText = 'Pay Now', buttonOnClick = async () => { }, onComplete = () => { }, asModal = true }) => {
    const [dialogIsOpen, setIsOpen] = useState(false)
    const [paymentIntent, setPaymentIntent] = useState(null)
    const [cards, setCards] = React.useState([])
    const [selectedCard, setSelectedCard] = React.useState(null)
    const [isDirectDebit, setIsDirectDebit] = React.useState(warranty.payment_method === 'direct_debit')
    const [newPaymentMethod, setNewPaymentMethod] = useState(false)
    const { activeDealer, dealerList } = useSelector((state) => state.dealersList.data)

    const [directDebitData, setDirectDebitData] = useState({
        schedule: null,
        setup: false,
        payment: {
            date: null,
            paid_at: null,
            paid: false,
            failed: false
        }
    })

    const id = warranty.id

    const isDealerActive = async () => {
        try {
            const resp = await apiGetMe()
            if (resp.data) {
                const data = resp.data
                const directDebitPayment = warranty.direct_debit_payment || {}

                if (data?.level && data.level === 'admin') {
                    if (activeDealer && activeDealer.value) {
                        setDirectDebitData({
                            schedule: dealerList.find(dealer => dealer.id === activeDealer.value).direct_debit_schedule,
                            setup: dealerList.find(dealer => dealer.id === activeDealer.value).direct_debit_setup,
                            payment: {
                                ...directDebitPayment
                            }
                        })
                    }
                } else if (data?.level && data.level === 'dealer') {
                    setDirectDebitData({
                        schedule: data?.dealer.direct_debit_schedule || null,
                        setup: data?.dealer.direct_debit_setup || false,
                        payment: {
                            ...directDebitPayment
                        }
                    })
                }
            } else {
                throw new Error('User session error, please login again.')
            }
        } catch (err) {
            popNotification("Warning", err.message, 'warn')
            return false
        }
    }


    useEffect(() => {
        if (!asModal) {
            console.log('PaymentModal', warranty, asModal);
            isDealerActive()
            openDialog()
            return;
        }

        if (dialogIsOpen) {
            isDealerActive()
        }
    }, [dialogIsOpen, asModal])


    const getPaymentURL = async () => {
        if (id) {
            try {
                const resp = await apiWarrantyPaymentIntent(id, selectedCard)

                if (resp && resp.status === 201) {
                    if (selectedCard) {
                        const last_four = cards.find(card => card.id === selectedCard).last_four

                        popNotification('Payment Successful', <p>
                            Payment Method: •••• {last_four}<br />
                            {warranty.vehicle.vrn} warranty is active
                        </p>, 'success', 5000)

                        const resp = await apiUpdatePaymentMethodTo(id, 'card')
                        if (resp && resp.status !== 200) {
                            popNotification('Error Updating Payment Method', resp.message)
                        }

                        onDialogClose(true)
                        return
                    }

                    setPaymentIntent(resp.data.secret)
                    if (asModal) {
                        setIsOpen(true)
                    }
                }
                else popNotification(resp.message)
            } catch (err) {
                // if code is 422, it means the warranty is already paid for
                if (err.response.status === 422) {
                    popNotification('Guarantee is already paid for')
                    onDialogClose(true)
                }

                popNotification(err.response.data.message)
            }
        } else {
            popNotification('Something went wrong. Please try again')
        }
    }

    const getMaybeSavedCards = async () => {
        try {
            const response = await apiGetCustomerSavedCards(warranty.customer.id)
            if (response && response.status === 200) {
                const cards = response.data
                setCards(cards)
                return cards.length > 0
            }

            return false
        } catch (err) {
            popNotification(err.response.data.message)

            return false
        }
    }

    const updatePaymentMethod = async () => {
        const paymentCollectionDate = getDirectDebitSchedule(directDebitData, true)

        if (warranty.payment_method === 'direct_debit') {
            popNotification('Payment Method Updated', `Payment will be collected on ${paymentCollectionDate}`)
            onDialogClose()
            return
        }

        try {
            const resp = await apiUpdateWarrantyToDirectDebit(id)
            if (resp && resp.status === 200) {
                popNotification('Payment Method Updated', `Payment will be collected on ${paymentCollectionDate}`)
                onDialogClose(true)
                return
            }
        } catch (err) {
            popNotification(err.response.data.message)
        }
    }

    const openDialog = async () => {
        // Prop passed down
        await buttonOnClick()

        // Get saved cards
        const hasCards = await getMaybeSavedCards()

        if (hasCards) {
            if (asModal) {
                setIsOpen(true)
                return
            }
        }

        getPaymentURL()
    }

    const renderSavedCards = () => {
        return (
            <div className="rounded-lg border border-gray-600 w-full mt-7">
                {cards.length === 0 && (
                    <div className="p-4 text-center">
                        No saved cards found
                    </div>
                )}
                {cards?.map((card, index) => (
                    <div
                        key={card.last_four}
                        className={classNames(
                            'flex flex-col md:flex-row lg:flex-row lg:items-center justify-between gap-3 p-4',
                            !isLastChild(cards, index) && 'border-b bborder-gray-600',
                            card.id === selectedCard && 'bg-gray-800'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            {card.brand === 'visa' && (
                                <img
                                    src="/img/others/img-8.png"
                                    alt="visa"
                                />
                            )}
                            {card.brand === 'mastercard' && (
                                <img
                                    src="/img/others/img-9.png"
                                    alt="master"
                                />
                            )}
                            <div>
                                <div className="flex items-center">
                                    <div className={`${card.id === selectedCard ? "text-white" : "text-black"} font-semibold`}>
                                        {warranty.customer.first_name} ••••{' '}
                                        {card.last_four}
                                    </div>

                                </div>
                                <span>
                                    Expires{' '}
                                    {
                                        months[
                                        parseInt(card.expMonth) - 1
                                        ]
                                    }{' '}
                                    {card.exp_year}
                                </span>
                            </div>
                        </div>
                        <div className="flex">
                            <Button
                                size="sm"
                                type="button"
                                className="!text-black mr-2"
                                color="custom-900"
                                variant="solid"
                                onClick={() =>
                                    popNotification('Not implemented yet')
                                }
                            >
                                Delete
                            </Button>
                            <Button
                                size="sm"
                                variant="solid"
                                onClick={() => {
                                    setSelectedCard(selectedCard === card.id ? null : card.id)
                                    setIsDirectDebit(false)
                                }}
                            >
                                {card.id === selectedCard ? 'Unselect' : 'Select'}
                            </Button>
                        </div>
                    </div>
                ))}

                <div
                    className={classNames(
                        'border-b border-gray-200 dark:border-gray-600'
                    )}
                />
                <span
                    className="cursor-pointer text-black !px-4 !py-2 font-semibold mx-auto flex justify-center items-center my-3"
                    size="sm"
                    type="button"
                    onClick={() => {
                        setNewPaymentMethod(true)
                        getPaymentURL()
                    }}
                >
                    <i className="fas fa-plus mr-1.5"></i> Use new card
                </span>
            </div>
        )
    }

    const onDialogClose = (runCallBk = false) => {
        // if (!asModal) return;
        if (runCallBk) {
            onComplete && onComplete()
        }

        setIsOpen(false)
    }

    const renderModalBody = () => {
        return (
            <>
                {directDebitData.setup && (
                    <div className="rounded-lg border border-gray-600 w-full my-5">
                        <div
                            className={classNames(
                                'flex flex-col md:flex-row lg:flex-row lg:items-center justify-between gap-3 p-4',
                                isDirectDebit && 'bg-gray-800'
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <div>
                                    <div className="flex items-center">
                                        <div className={`${isDirectDebit ? "text-white" : "text-black"} font-semibold`}>
                                            Pay on Account
                                        </div>

                                    </div>
                                    <span>
                                        {getDirectDebitSchedule(directDebitData)}
                                    </span>
                                </div>
                            </div>
                            <div className="flex">
                                <Button
                                    size="sm"
                                    variant="solid"
                                    onClick={() => {
                                        setIsDirectDebit(!isDirectDebit)
                                        setSelectedCard(null)
                                    }}
                                >
                                    {isDirectDebit ? 'Unselect' : 'Select'}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {!newPaymentMethod && renderSavedCards()}

                {/* if payment intent AND !direct debit And newpayment method
                or if there's no saved cards
                */}
                {(paymentIntent && !isDirectDebit && newPaymentMethod)  ? (
                    <div className='mt-4'>
                        <StripePaymentElement
                            payment_intent={paymentIntent}
                            return_url={`${window.location.href}`}
                            onCancel={() => {
                                setNewPaymentMethod(false)
                            }}
                        />
                    </div>
                ) : null}

                {(selectedCard || isDirectDebit) && (
                    <div className="flex justify-end gap-2 mt-4">
                        <Button
                            size="sm"
                            type="button"
                            variant="solid"
                            onClick={() => {
                                if (selectedCard) {
                                    getPaymentURL()
                                } else if (isDirectDebit) {
                                    updatePaymentMethod()
                                }
                            }}
                        >
                            Pay Now
                        </Button>
                    </div>
                )}
            </>
        )
    }

    if (!asModal) {
        return (
            <div className='w-full'>
                {renderModalBody()}
            </div>
        )
    }

    return (
        <div>
            <Button
                size="sm"
                type="button"
                variant="solid"
                onClick={openDialog}
            >
                {buttonText}
            </Button>
            <Dialog
                // dialogClass="bg-custom-900"
                isOpen={dialogIsOpen}
                onClose={() => onDialogClose()}
                onRequestClose={() => onDialogClose()}
                shouldCloseOnOverlayClick={false}
                shouldCloseOnEsc={false}
                width={670}
                fullScreen={false}
                dialogTitle={<h3 className="dialog-title text-xl flex gap-2 items-center text-black"><HiCurrencyPound /> Payment</h3>}
            >
                {renderModalBody()}
            </Dialog>
        </div>
    )
}

// memoize
export default React.memo(PaymentModal)